import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { fetchWinBackConversions } from '../../services/api';
import './ConversionTracker.css';

const ConversionTracker = ({ campanhaId, dateRange }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const response = await fetchWinBackConversions(campanhaId, dateRange);
                if (response.success) {
                    setData(response.data);
                } else {
                    setData(generateMockData());
                }
            } catch (err) {
                console.error('Erro ao carregar conversões:', err);
                setData(generateMockData());
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [campanhaId, dateRange]);

    const generateMockData = () => {
        return {
            summary: {
                totalContatos: 0,
                cliques: 0,
                taxaClique: 0,
                conversoes: 0,
                taxaConversao: 0,
                valorRecuperado: 0,
                ticketMedio: 0,
                tempoMedioRecuperacao: 0,
                roi: 0
            },
            porCanal: [],
            porSegmento: [],
            ultimasConversoes: []
        };
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Carregando métricas de conversão...</p>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="conversion-tracker">
            {/* KPIs de Conversão */}
            <Row className="g-3 mb-4">
                <Col lg={3} md={6}>
                    <Card className="metric-card metric-clicks h-100">
                        <Card.Body>
                            <div className="metric-value">{data.summary.cliques}</div>
                            <div className="metric-label">Cliques no Link</div>
                            <div className="metric-rate">
                                Taxa: <strong>{data.summary.taxaClique}%</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={3} md={6}>
                    <Card className="metric-card metric-conversions h-100">
                        <Card.Body>
                            <div className="metric-value">{data.summary.conversoes}</div>
                            <div className="metric-label">Conversões</div>
                            <div className="metric-rate">
                                Taxa: <strong>{data.summary.taxaConversao}%</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={3} md={6}>
                    <Card className="metric-card metric-revenue h-100">
                        <Card.Body>
                            <div className="metric-value">
                                R$ {data.summary.valorRecuperado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                            <div className="metric-label">Valor Recuperado</div>
                            <div className="metric-rate">
                                ROI: <strong>{data.summary.roi > 0 ? '+' : ''}{data.summary.roi}%</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={3} md={6}>
                    <Card className="metric-card metric-time h-100">
                        <Card.Body>
                            <div className="metric-value">{data.summary.tempoMedioRecuperacao}</div>
                            <div className="metric-label">Dias Médio p/ Conversão</div>
                            <div className="metric-rate">
                                Ticket: <strong>R$ {data.summary.ticketMedio.toFixed(2)}</strong>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                {/* Desempenho por Canal */}
                <Col lg={6}>
                    <Card className="chart-card h-100">
                        <Card.Header className="bg-white">
                            <h6 className="mb-0 fw-semibold">Desempenho por Canal</h6>
                        </Card.Header>
                        <Card.Body>
                            {data.porCanal.length > 0 ? (
                                <Table hover size="sm" className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Canal</th>
                                            <th className="text-center">Contatos</th>
                                            <th className="text-center">Cliques</th>
                                            <th className="text-center">Conversões</th>
                                            <th className="text-end">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.porCanal.map((canal, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <Badge bg={
                                                        canal.canal === 'WhatsApp' ? 'success' :
                                                            canal.canal === 'Email' ? 'primary' : 'secondary'
                                                    } className="me-2">
                                                        {canal.canal === 'WhatsApp' ? 'WA' :
                                                            canal.canal === 'Email' ? 'EM' : 'SM'}
                                                    </Badge>
                                                    {canal.canal}
                                                </td>
                                                <td className="text-center">{canal.contatos}</td>
                                                <td className="text-center">
                                                    {canal.cliques}
                                                    {canal.contatos > 0 && (
                                                        <small className="text-muted ms-1">
                                                            ({Math.round((canal.cliques / canal.contatos) * 100)}%)
                                                        </small>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Badge bg="success">{canal.conversoes}</Badge>
                                                </td>
                                                <td className="text-end fw-semibold">
                                                    R$ {canal.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center text-muted py-4">
                                    Nenhum dado de canal disponível
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Taxa de Conversão por Segmento */}
                <Col lg={6}>
                    <Card className="chart-card h-100">
                        <Card.Header className="bg-white">
                            <h6 className="mb-0 fw-semibold">Conversão por Segmento</h6>
                        </Card.Header>
                        <Card.Body>
                            {data.porSegmento.length > 0 ? (
                                data.porSegmento.map((seg, idx) => (
                                    <div key={idx} className="segment-bar mb-3">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-sm">{seg.segmento}</span>
                                            <span>
                                                <Badge bg="success" className="me-2">{seg.conversoes}</Badge>
                                                <strong>{seg.taxa}%</strong>
                                            </span>
                                        </div>
                                        <ProgressBar
                                            now={parseFloat(seg.taxa) || 0}
                                            variant={
                                                parseFloat(seg.taxa) >= 40 ? 'success' :
                                                    parseFloat(seg.taxa) >= 25 ? 'info' :
                                                        parseFloat(seg.taxa) >= 15 ? 'warning' : 'secondary'
                                            }
                                            style={{ height: '8px', borderRadius: '4px' }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="text-center text-muted py-4">
                                    Nenhum dado de segmento disponível
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Últimas Conversões */}
            <Card className="chart-card">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-semibold">Últimas Conversões</h6>
                    {data.ultimasConversoes.length > 0 && (
                        <Badge bg="success">
                            +{data.ultimasConversoes.length} esta semana
                        </Badge>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {data.ultimasConversoes.length > 0 ? (
                        <Table hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th>Cliente</th>
                                    <th>Data</th>
                                    <th>Produto</th>
                                    <th className="text-center">Tempo p/ Conversão</th>
                                    <th className="text-end">Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.ultimasConversoes.map((conv, idx) => (
                                    <tr key={idx}>
                                        <td className="fw-medium">{conv.cliente}</td>
                                        <td>{new Date(conv.data).toLocaleDateString('pt-BR')}</td>
                                        <td>
                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                                                {conv.produto}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg={conv.diasRecuperacao <= 2 ? 'success' : conv.diasRecuperacao <= 5 ? 'warning' : 'secondary'}>
                                                {conv.diasRecuperacao} {conv.diasRecuperacao === 1 ? 'dia' : 'dias'}
                                            </Badge>
                                        </td>
                                        <td className="text-end fw-semibold text-success">
                                            R$ {conv.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center text-muted py-4">
                            Nenhuma conversão registrada ainda
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default ConversionTracker;
